/**
 * PaneTakeoverDrawer — the frame every full-frame takeover in the Outlook pane
 * is mounted in.
 *
 * The chrome INSIDE a takeover was unified first (`WorkflowCenterBar` /
 * `TakeoverBackHeader` / `TakeoverTitleHeader`); this is the container those
 * three sit in, unified for the same reason. Joachim compared the run drawer
 * with the workflow picker (2026-09-21): identical `<Drawer>` props, visibly
 * different entrances — the picker slid up, the run "popped". Two call sites
 * that must match cannot be kept matching by copying prop objects between
 * them, so there is now one.
 *
 * WHAT IT FIXES, and what it cannot. `appear` is set explicitly: a Drawer that
 * MOUNTS already-open only animates if the transition is told to animate on
 * appear, and the run drawer does mount already-open because App.tsx re-keys
 * it (`key={...-${drawerOpenCount}}`) in the same handler that sets
 * `open: true`, to clear stale form values. What this cannot fix is the COST
 * of that remount — a fresh InstanceDetailDrawer re-runs every hook,
 * subscription and fetch it owns at exactly the moment the slide should be
 * painting, and dropped frames read as a pop too. If a run still enters
 * differently after this, the key is the remaining cause and the fix is to
 * stop remounting, not to tune the transition.
 *
 * Shared — no Office.js, no Remix import.
 */
import { Drawer } from "@mui/material";
import type { ReactNode } from "react";

/** Matches the pane's existing feel; both takeovers used these already. */
export const TAKEOVER_TRANSITION_MS = { enter: 400, exit: 350 } as const;

export interface PaneTakeoverDrawerProps {
  open: boolean;
  /** Esc and the backdrop both land here, on top of whatever explicit back /
   *  Close control the caller renders inside. */
  onClose: () => void;
  children: ReactNode;
}

export function PaneTakeoverDrawer({
  open,
  onClose,
  children,
}: PaneTakeoverDrawerProps) {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      transitionDuration={TAKEOVER_TRANSITION_MS}
      // Animate even when this mounts already-open — see the note above.
      SlideProps={{ appear: true }}
      PaperProps={{
        sx: {
          // Full frame, not an 85vh sheet: a takeover that leaves the
          // AppShell hero peeking out is a sheet, and reads as one.
          height: "100vh",
          maxHeight: "100vh",
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {children}
    </Drawer>
  );
}

export default PaneTakeoverDrawer;
